import type { Locale } from '@change-corgi/config/locales';
import type { ReportOptions } from '@change-corgi/core/errorReporter/common';
import { addQueryParam, retry } from '@change-corgi/core/http';
import type { Translations } from '@change-corgi/core/i18n';
import type { Utilities } from '@change-corgi/core-react/utilityContext';

import { getS3TranslationsUrl, getServerTranslationsUrl, getTranslationsOverrides } from '#src/shared/i18n';

const RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 250;

// Sentry groups by exception type (error.name) + stacktrace, so each i18n failure
// kind gets a distinct name to split genuine HTTP/parse failures (worth alerting
// on) from client-side network failures, which are unactionable noise dominating
// this issue's volume.
const I18N_FAILURE_KINDS = {
	http: { errorName: 'I18nHttpError', reportOptions: undefined },
	parse: { errorName: 'I18nParseError', reportOptions: undefined },
	network: { errorName: 'I18nNetworkError', reportOptions: { severity: 'warning' } },
} satisfies Record<string, { errorName: string; reportOptions: ReportOptions | undefined }>;

export type TranslationsOptions = {
	locale: Locale;
	cacheBuster?: string;
	/**
	 * Whether to use the front-end server to retrieve translations
	 *
	 * if false, translations will be retrieved from S3
	 */
	useServerTranslations: boolean;
};

export async function getTranslations(
	{ http, errorReporter }: Pick<Utilities, 'http' | 'errorReporter'>,
	{ locale, cacheBuster, useServerTranslations }: TranslationsOptions,
): Promise<Translations> {
	const overrides = getTranslationsOverrides(locale);
	if (overrides) {
		return Promise.resolve(overrides);
	}

	const i18nUrl = useServerTranslations
		? getServerTranslationsUrl(locale, cacheBuster)
		: getS3TranslationsUrl(locale, cacheBuster);

	// use getAsset to avoid sending custom headers, which seem to add 500ms of queuing to the request (at least in Chrome)
	return (
		retry({
			task: async () => http.getAsset<Translations>(i18nUrl),
			onRetry: async () => http.getAsset<Translations>(addQueryParam(i18nUrl, 'retrycb', String(Date.now()))),
			attempts: RETRY_ATTEMPTS,
			delay: RETRY_DELAY,
		})
			// eslint-disable-next-line promise/prefer-await-to-then
			.catch((response) => {
				/* eslint-disable @typescript-eslint/no-unsafe-member-access */
				// A truthy status means the request completed with an HTTP error (a real
				// server/S3 failure). A SyntaxError means the response came back ok (2xx) but
				// `response.json()` couldn't parse the body — also a real server/deploy
				// issue, just one that never carries a status. Anything else has no status
				// and isn't a parse failure, i.e. `TypeError: Failed to fetch` — the request
				// never completed at the transport layer (the user's connection dropped,
				// they navigated away mid-load, offline, proxy/ad-blocker, etc.).
				let failureKind: keyof typeof I18N_FAILURE_KINDS = 'network';
				if (response.status) {
					failureKind = 'http';
				} else if (response instanceof SyntaxError) {
					failureKind = 'parse';
				}
				const isHttpError = failureKind === 'http';

				const { errorName, reportOptions } = I18N_FAILURE_KINDS[failureKind];
				const error = new Error(
					`Failed to retrieve i18n file (${failureKind.toUpperCase()}) error after ${RETRY_ATTEMPTS} attempts with ${RETRY_DELAY}ms delay`,
				);
				error.name = errorName;

				void errorReporter.report(
					{
						error,
						// surfaced as Sentry tags (searchable/filterable, usable in alert rules),
						// unlike params below which are only visible as extra event data
						context: { i18nFailureType: failureKind, ...(isHttpError && { status: response.status }) },
						params: {
							// eslint-disable-next-line @typescript-eslint/no-unsafe-call
							message: isHttpError ? response.statusText : response.toString(),
							url: i18nUrl,
							locale,
						},
					},
					reportOptions,
				);
				/* eslint-enable @typescript-eslint/no-unsafe-member-access */
				throw response;
			})
	);
}
